import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, Users, ShoppingBag, Sparkles, Palette, Crown, DollarSign, Rocket, Image, MapPin } from 'lucide-react';
import api from '../lib/api';
import SEOHead from '../components/SEOHead';
import LanguageSwitcher from '../components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/7b132e06-4e82-4691-87f6-190baf6c38e4/images/b3fa3f7b86950349117a97eaaf1243981fde94f83d3923a3bc5c126478ede0a0.png';

const features = [
  { icon: Sparkles, title: 'AI Product Creator', desc: 'Create ebooks, guides, planners and more with AI assistance.' },
  { icon: ShoppingBag, title: 'Digital Marketplace', desc: 'Sell premium digital products to a global audience.' },
  { icon: DollarSign, title: 'Automated Payouts', desc: 'Stripe Connect handles payments and seller payouts automatically.' },
  { icon: Palette, title: 'Design Studio', desc: 'Create custom t-shirts and print-on-demand products.' },
  { icon: Users, title: 'Creator Communities', desc: 'Connect with fellow creators in public and private groups.' },
  { icon: Crown, title: 'Inner Circle', desc: 'Exclusive membership for premium content and early access.' },
];

const getImageSrc = (img) => {
  if (!img) return null;
  if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('data:')) return img;
  return `data:image/jpeg;base64,${img}`;
};

export default function LandingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [wallpaperOfDay, setWallpaperOfDay] = useState(null);
  const [loadingWallpaper, setLoadingWallpaper] = useState(true);
  const [newestProducts, setNewestProducts] = useState([]);
  const [featuredCreators, setFeaturedCreators] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [wpRes, prodRes, creatRes] = await Promise.allSettled([
          api.get('/wallpaper/of-the-day'),
          api.get('/public/newest-products?limit=4'),
          api.get('/public/featured-creators?limit=6'),
        ]);
        if (wpRes.status === 'fulfilled') setWallpaperOfDay(wpRes.value.data);
        if (prodRes.status === 'fulfilled') setNewestProducts(prodRes.value.data.products || []);
        if (creatRes.status === 'fulfilled') setFeaturedCreators(creatRes.value.data.creators || []);
      } catch {} finally {
        setLoadingWallpaper(false);
      }
    };
    fetchAll();
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title={null}
        description="Susan David Creator Platform — Create premium digital products, build your creator brand, and sell from one elegant platform. AI-powered ebooks, wallpapers, digital products, and community."
        url={window.location.origin}
      />

      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-amber-100/40">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-14 px-4 md:px-8">
          <span className="font-serif text-base tracking-tight">
            <span className="text-obsidian">Susan David</span>
            <span className="text-amber-500 ml-1.5 font-light">Creator Platform</span>
          </span>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/marketplace">
              <Button variant="ghost" className="text-xs h-9 px-4 hidden md:flex text-obsidian/70 hover:text-amber-700">{t('landing.nav_marketplace')}</Button>
            </Link>
            <Link to="/creators">
              <Button variant="ghost" className="text-xs h-9 px-4 hidden md:flex text-obsidian/70 hover:text-amber-700">{t('landing.nav_creators')}</Button>
            </Link>
            <Link to="/pricing">
              <Button variant="ghost" className="text-xs h-9 px-4 hidden md:flex text-obsidian/70 hover:text-amber-700">{t('landing.nav_pricing')}</Button>
            </Link>
            <Link to={user ? '/feed' : '/auth'}>
              <Button data-testid="landing-cta-nav" className="rounded-none text-[10px] uppercase tracking-[0.2em] h-9 px-6 bg-obsidian text-white hover:bg-amber-600 transition-colors duration-500">
                {user ? t('landing.enter') : t('landing.get_started')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        {/* Subtle warm wash */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/30 via-white to-white" />
        <div className="absolute top-0 right-0 w-[60%] h-full bg-gradient-to-l from-amber-50/40 to-transparent" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-center min-h-[85vh] py-16 lg:py-0">
            {/* Left: Copy */}
            <div className="order-2 lg:order-1 max-w-xl">
              <div className="inline-flex items-center gap-2 border border-amber-200/60 bg-amber-50/40 px-4 py-1.5 mb-8">
                <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-amber-700 font-medium">{t('landing.badge')}</span>
              </div>

              <h1
                className="font-serif text-3xl sm:text-4xl lg:text-[2.8rem] text-obsidian leading-[1.15] mb-6 tracking-tight"
                data-testid="hero-headline"
              >
                {t('landing.hero_title_1')}{' '}
                <span className="text-amber-600 italic">{t('landing.hero_title_accent')}</span> {t('landing.hero_title_2')}
              </h1>

              <p className="text-sm md:text-base text-obsidian/55 mb-10 leading-relaxed max-w-md font-light">
                {t('landing.hero_subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={user ? '/ai-product-creator' : '/auth'}>
                  <Button
                    data-testid="landing-hero-cta"
                    className="rounded-none text-[10px] uppercase tracking-[0.2em] h-12 px-10 bg-obsidian text-white hover:bg-amber-600 transition-colors duration-500 shadow-lg shadow-obsidian/10 font-medium"
                  >
                    {t('landing.start_creating')}
                  </Button>
                </Link>
                <Link to="/marketplace">
                  <Button
                    data-testid="landing-marketplace-cta"
                    variant="outline"
                    className="rounded-none text-[10px] uppercase tracking-[0.2em] h-12 px-10 border border-obsidian/15 text-obsidian hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50/50 transition-all duration-500 font-medium"
                  >
                    {t('landing.explore_marketplace')}
                  </Button>
                </Link>
              </div>

              {/* Trust signals */}
              <div className="mt-12 flex items-center gap-6 text-[10px] text-obsidian/35 uppercase tracking-[0.15em]">
                <span>{t('landing.trust_stripe')}</span>
                <div className="w-px h-3 bg-amber-200/60" />
                <span>{t('landing.trust_ai')}</span>
                <div className="w-px h-3 bg-amber-200/60" />
                <span>{t('landing.trust_pod')}</span>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative">
                {/* Gold accent frame */}
                <div className="absolute -inset-3 bg-gradient-to-br from-amber-100/40 via-transparent to-amber-50/30 -z-10" />
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-amber-100/30 -z-10" />
                <img
                  src={HERO_IMAGE}
                  alt="Susan David Creator Platform"
                  className="w-full max-w-md lg:max-w-lg h-auto object-cover shadow-2xl shadow-amber-100/40"
                  data-testid="hero-image"
                  loading="eager"
                />
                {/* Small overlay badge */}
                <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm px-4 py-2 shadow-lg">
                  <p className="text-[9px] uppercase tracking-[0.25em] text-amber-700 font-medium">Susan David</p>
                  <p className="text-[8px] text-obsidian/40 uppercase tracking-wider">Creator Platform</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Thin gold divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-amber-300/50 to-transparent" />

      {/* Featured Gallery Collection — Premium Framed Artwork */}
      <section className="py-20 md:py-32 bg-stone-50" data-testid="featured-gallery-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center">
            {/* Premium Badge */}
            <div className="inline-block mb-10">
              <span
                className="text-[10px] uppercase tracking-[0.35em] font-semibold px-5 py-2 border border-amber-300/70 text-amber-800 bg-gradient-to-r from-amber-50 to-ivory"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="gallery-badge"
              >
                Premium Collection
              </span>
            </div>

            {/* Framed Artwork */}
            <div className="relative mx-auto max-w-lg group">
              {/* Soft ambient shadow behind the frame */}
              <div className="absolute -inset-6 bg-gradient-to-b from-stone-200/40 via-stone-300/20 to-transparent rounded-sm blur-2xl opacity-80" />

              {/* The Frame */}
              <div className="relative" data-testid="gallery-frame">
                {/* Outer frame border — dark wood tone */}
                <div className="bg-gradient-to-br from-stone-700 via-stone-800 to-stone-900 p-[10px] shadow-2xl shadow-stone-400/40">
                  {/* Inner bevel — gold accent */}
                  <div className="bg-gradient-to-br from-amber-200/80 via-amber-100/60 to-amber-200/80 p-[3px]">
                    {/* Matte / passepartout */}
                    <div className="bg-ivory p-5 sm:p-7">
                      {/* The Artwork */}
                      {loadingWallpaper ? (
                        <div className="aspect-[4/3] bg-stone-100 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
                        </div>
                      ) : wallpaperOfDay?.wallpaper ? (
                        <div className="aspect-[4/3] overflow-hidden relative">
                          {wallpaperOfDay.wallpaper.cover_image || wallpaperOfDay.wallpaper.images?.[0] ? (
                            <img
                              src={wallpaperOfDay.wallpaper.cover_image || wallpaperOfDay.wallpaper.images[0]}
                              alt={wallpaperOfDay.wallpaper.title}
                              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                              data-testid="gallery-artwork-image"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-stone-200 via-amber-50 to-rose-50 flex items-center justify-center">
                              <Image size={48} className="text-stone-300" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="aspect-[4/3] bg-gradient-to-br from-stone-100 via-amber-50 to-rose-50 flex items-center justify-center">
                          <Image size={48} className="text-stone-300" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Collection Info — below the frame */}
            <div className="mt-10 space-y-3">
              <h2
                className="font-serif text-2xl sm:text-3xl md:text-4xl text-obsidian tracking-tight"
                data-testid="gallery-collection-title"
              >
                Neural Portrait Collection
              </h2>

              <p className="text-xs uppercase tracking-[0.25em] text-stone-400 font-medium">
                Susan David Premium Interior Collection
              </p>

              {/* Decorative rule */}
              <div className="flex items-center justify-center gap-3 pt-2">
                <div className="h-px w-8 bg-amber-300/60" />
                <div className="w-1.5 h-1.5 rotate-45 border border-amber-300/80" />
                <div className="h-px w-8 bg-amber-300/60" />
              </div>

              {wallpaperOfDay?.wallpaper && !wallpaperOfDay.wallpaper.is_placeholder && (
                <p className="text-sm text-stone-500 font-light max-w-md mx-auto leading-relaxed pt-1">
                  {wallpaperOfDay.wallpaper.short_description || 'A curated selection of premium artworks for discerning collectors and interiors.'}
                </p>
              )}
            </div>

            {/* CTA */}
            <div className="mt-10">
              <Link to="/wallpapers">
                <Button
                  data-testid="gallery-enter-btn"
                  className="rounded-none h-12 px-12 bg-obsidian text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-stone-800 transition-colors duration-500 shadow-lg shadow-obsidian/10"
                >
                  Enter Gallery
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Newest Products Section */}
      {newestProducts.length > 0 && (
        <section className="py-16 md:py-24 bg-white" data-testid="newest-products-section">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-amber-600 text-xs uppercase tracking-[0.3em] mb-2 font-medium">Just Listed</p>
                <h2 className="font-serif text-2xl md:text-3xl text-obsidian">Newest Products</h2>
              </div>
              <Link to="/marketplace">
                <Button variant="outline" className="rounded-full text-xs border-obsidian/20 hover:border-amber-400">
                  View All <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {newestProducts.map((p) => {
                const imgSrc = getImageSrc(p.images?.[0]);
                return (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    className="group bg-white border border-border/40 rounded-2xl overflow-hidden hover:border-amber-300/50 hover:shadow-xl hover:shadow-amber-100/30 transition-all duration-500"
                    data-testid={`newest-product-${p.id}`}
                  >
                    <div className="aspect-square bg-stone-50 relative overflow-hidden">
                      {imgSrc ? (
                        <img src={imgSrc} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={32} className="text-muted-foreground/20" /></div>
                      )}
                      <Badge className="absolute bottom-3 left-3 bg-white/90 text-obsidian rounded-full text-[10px] uppercase backdrop-blur-sm">{p.category}</Badge>
                    </div>
                    <div className="p-4">
                      <h3 className="font-serif text-sm mb-1 group-hover:text-amber-700 transition-colors truncate">{p.title}</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground truncate">{p.seller_name}</span>
                        <span className="text-sm font-semibold text-amber-600">{'\u20AC'}{p.price?.toFixed(2)}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Creators Section */}
      {featuredCreators.length > 0 && (
        <section className="py-16 md:py-24 bg-gradient-to-br from-stone-50 via-amber-50/30 to-rose-50/40" data-testid="featured-creators-section">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-amber-600 text-xs uppercase tracking-[0.3em] mb-2 font-medium">Top Talent</p>
                <h2 className="font-serif text-2xl md:text-3xl text-obsidian">Featured Creators</h2>
              </div>
              <Link to="/creators">
                <Button variant="outline" className="rounded-full text-xs border-obsidian/20 hover:border-amber-400">
                  View All <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCreators.map((c) => (
                <Link
                  key={c.id}
                  to={`/profile/${c.id}`}
                  className="group bg-white border border-border/40 rounded-2xl p-6 hover:border-amber-300/50 hover:shadow-xl hover:shadow-amber-100/30 transition-all duration-500"
                  data-testid={`featured-creator-${c.id}`}
                >
                  <div className="flex items-start gap-4 mb-3">
                    <Avatar className="h-12 w-12 border-2 border-amber-100 group-hover:border-amber-300 transition-colors">
                      <AvatarFallback className="bg-gradient-to-br from-amber-100 to-rose-100 text-amber-700 font-serif text-lg">
                        {(c.display_name || c.username || 'C')[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-base group-hover:text-amber-700 transition-colors truncate">{c.display_name || c.username}</h3>
                      <p className="text-xs text-muted-foreground">@{c.username}</p>
                    </div>
                  </div>
                  {c.bio && <p className="text-xs text-muted-foreground font-light line-clamp-2 mb-3">{c.bio}</p>}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><ShoppingBag size={12} /> {c.product_count} products</span>
                    {c.country && <span className="flex items-center gap-1"><MapPin size={12} /> {c.country}</span>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-20 md:py-32" data-testid="features-section">
        <p className="text-amber-600 text-xs uppercase tracking-[0.3em] mb-3 text-center font-medium">Everything You Need</p>
        <h2 className="font-serif text-2xl md:text-3xl text-center mb-4">Built for <em className="text-amber-600">Premium Creators</em></h2>
        <p className="text-center text-muted-foreground mb-16 max-w-xl mx-auto text-sm">From AI-powered product creation to automated payouts, we handle the tech so you can focus on creating.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className="group bg-gradient-to-br from-white to-rose-50/30 border border-border/40 p-8 rounded-2xl hover:border-amber-300/50 hover:shadow-xl hover:shadow-amber-100/50 transition-all duration-500 animate-in"
              style={{ animationDelay: `${i * 100}ms` }}
              data-testid={`feature-card-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <f.icon size={22} className="text-amber-600" />
              </div>
              <h3 className="font-serif text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it Works */}
      <section className="bg-gradient-to-br from-obsidian to-obsidian/95 text-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-amber-400 text-xs uppercase tracking-[0.3em] mb-3 text-center font-medium">Simple Process</p>
          <h2 className="font-serif text-2xl md:text-3xl text-center mb-16">Idea to Income in 4 Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Create', desc: 'AI generates your product content, cover concepts, and mockup directions.' },
              { step: '02', title: 'Price', desc: 'Get AI-powered pricing suggestions based on market analysis.' },
              { step: '03', title: 'Publish', desc: 'One-click publish to the marketplace with full descriptions.' },
              { step: '04', title: 'Earn', desc: 'Stripe handles payments. Gelato fulfills print orders. You get paid.' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-rose-500 text-obsidian font-serif text-xl mb-4">
                  {item.step}
                </div>
                <h3 className="font-serif text-lg mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm font-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-serif text-2xl md:text-3xl mb-4 text-obsidian">Ready to Create?</h2>
          <p className="text-obsidian/60 mb-8 font-light text-base">Join thousands of creators building and selling premium digital products.</p>
          <Link to="/auth">
            <Button
              data-testid="landing-bottom-cta"
              className="rounded-full text-sm h-14 px-12 bg-obsidian text-white hover:bg-amber-600 transition-all duration-300 shadow-xl font-medium"
            >
              Create Your Account <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
          <Link to="/inner-circle">
            <Button
              data-testid="landing-inner-circle-cta"
              variant="ghost"
              className="ml-4 rounded-full text-sm h-14 px-8 text-obsidian hover:text-amber-700 transition-colors duration-300 font-medium"
            >
              <Crown size={16} className="mr-2 text-amber-500" /> Inner Circle
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground font-sans">Susan David Creator Platform</p>
          <div className="flex items-center gap-6">
            <Link to="/rules" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Community Rules</Link>
            <Link to="/marketplace" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Marketplace</Link>
            <Link to="/creators" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Creators</Link>
            <Link to="/auth" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
